import { differenceInYears, subYears } from 'date-fns';
import { FunctionComponent, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { OrganisationContext, UserContext } from '../../../context';
import {
  AnalyticChartTableTotalSeries,
  useInAppMessageTotalQuery,
  usePushNotificationTotalQuery,
} from '../../../services';
import {
  Button,
  ButtonType,
  Delta,
  getChannelDomain,
  getWebAppUrl,
  IconLabel,
  IconLabelHintDirection,
} from '../../../shared';

import classes from './NotificationsRightSide.module.scss';

interface RenderStatsArgs {
  notificationTotal?: AnalyticChartTableTotalSeries;
  isFetchingNotificationTotal: boolean;
  notificationTotalPrev?: AnalyticChartTableTotalSeries;
  isFetchingNotificationTotalPrev: boolean;
}

export const NotificationsRightSide: FunctionComponent = () => {
  const { t } = useTranslation();

  const { role: organisationRole } = useContext(OrganisationContext).organisation;

  const { channelId } = useContext(UserContext).userInfo.userData;

  const date = new Date();

  date.setSeconds(0, 0);

  const to = date.toISOString();

  const from = subYears(date, 1).toISOString();

  const { data: pushNotificationTotal, isFetching: isFetchingPushNotificationTotal } =
    usePushNotificationTotalQuery({ from, to, channelId: [channelId] });

  const { data: inAppMessageTotal, isFetching: isFetchingInAppMessageTotal } =
    useInAppMessageTotalQuery({ from, to, channelId: [channelId] });

  const yearsDiff = differenceInYears(new Date(to), new Date(from)) + 1;

  const { data: pushNotificationTotalPrev, isFetching: isFetchingPushNotificationTotalPrev } =
    usePushNotificationTotalQuery({
      from: subYears(new Date(from), yearsDiff).toISOString(),
      to: subYears(new Date(to), yearsDiff).toISOString(),
      channelId: [channelId],
    });

  const { data: inAppMessageTotalPrev, isFetching: isFetchingInAppMessageTotalPrev } =
    useInAppMessageTotalQuery({
      from: subYears(new Date(from), yearsDiff).toISOString(),
      to: subYears(new Date(to), yearsDiff).toISOString(),
      channelId: [channelId],
    });

  const renderStats = useCallback(
    ({
      notificationTotal,
      isFetchingNotificationTotal,
      notificationTotalPrev,
      isFetchingNotificationTotalPrev,
    }: RenderStatsArgs) => {
      if (isFetchingNotificationTotal || isFetchingNotificationTotalPrev) {
        return <Skeleton height={'5rem'} />;
      }

      if (!notificationTotal || !notificationTotalPrev) {
        return null;
      }

      const { items } = notificationTotal;

      const { items: itemsPrev } = notificationTotalPrev;

      const stats = [
        {
          localiseKey: 'notificationListLabels.total',
          value: items.find(({ label }) => label === 'total')?.value ?? 0,
          valuePrev: itemsPrev.find(({ label }) => label === 'total')?.value ?? 0,
        },
        {
          localiseKey: 'notificationListLabels.total-open',
          value: items.find(({ label }) => label === 'openTotal')?.value ?? 0,
          valuePrev: itemsPrev.find(({ label }) => label === 'openTotal')?.value ?? 0,
        },
        {
          localiseKey: 'notificationListLabels.average-sent',
          value: items.find(({ label }) => label === 'sentAvg')?.value ?? 0,
          valuePrev: itemsPrev.find(({ label }) => label === 'sentAvg')?.value ?? 0,
        },
        {
          localiseKey: 'notificationListLabels.average-open',
          value: items.find(({ label }) => label === 'openAvg')?.value ?? 0,
          valuePrev: itemsPrev.find(({ label }) => label === 'openAvg')?.value ?? 0,
        },
      ];

      return (
        <>
          {stats.map(({ localiseKey, value, valuePrev }) => {
            return (
              <div key={localiseKey} className={classes['notifications-right-side__stats-item']}>
                <span className={classes['notifications-right-side__stats-item-label']}>
                  {t(localiseKey)}
                </span>
                <span className={classes['notifications-right-side__stats-item-data']}>
                  <Delta value={value} valuePrev={valuePrev} />
                  <span className={classes['notifications-right-side__stats-item-value']}>
                    {value}
                  </span>
                </span>
              </div>
            );
          })}
        </>
      );
    },
    [t]
  );

  return (
    <div className={classes['notifications-right-side']}>
      <div className={classes['notifications-right-side__info']}>
        <IconLabel
          iconId={'info'}
          color={'#888888'}
          data={t('notificationsPage.analytics-info')}
          hintDirection={IconLabelHintDirection.left}
          singleColor
        />
      </div>

      <div className={classes['notifications-right-side__stats']}>
        <h2 className={classes['notifications-right-side__stats-title']}>
          {t('notificationsPage.push')}
        </h2>

        {renderStats({
          notificationTotal: pushNotificationTotal,
          isFetchingNotificationTotal: isFetchingPushNotificationTotal,
          notificationTotalPrev: pushNotificationTotalPrev,
          isFetchingNotificationTotalPrev: isFetchingPushNotificationTotalPrev,
        })}
      </div>
      <div className={classes['notifications-right-side__stats']}>
        <h2 className={classes['notifications-right-side__stats-title']}>
          {t('notificationsPage.in-app')}
        </h2>

        {renderStats({
          notificationTotal: inAppMessageTotal,
          isFetchingNotificationTotal: isFetchingInAppMessageTotal,
          notificationTotalPrev: inAppMessageTotalPrev,
          isFetchingNotificationTotalPrev: isFetchingInAppMessageTotalPrev,
        })}
      </div>
      {organisationRole && (
        <Button
          type={ButtonType.secondary}
          label={t('notificationsPage.open-analytics')}
          onClick={() =>
            window.open(
              `${getWebAppUrl(getChannelDomain())}/organisation/analytics`,
              '_blank',
              'noopener, noreferrer'
            )
          }
          fullWidth
        />
      )}
    </div>
  );
};
